import { useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { CalendarIcon, Gift, Heart, ListChecks, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { createWishlist, type Wishlist, type WishlistType } from "@/services/wishlists";
import { cn } from "@/lib/utils";

const TYPES: { value: WishlistType; icon: typeof Gift; label: string }[] = [
  { value: "wishlist", icon: Gift, label: "Wishlist" },
  { value: "registry", icon: Heart, label: "Registry" },
  { value: "personal", icon: ListChecks, label: "Personal" },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: WishlistType;
  onTypeChange: (type: WishlistType) => void;
  onCreated: (wishlist: Wishlist) => void;
}

const CreateWishlistModal = ({ open, onOpenChange, type, onTypeChange, onCreated }: Props) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState<Date | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setTitle("");
    setDescription("");
    setEventDate(undefined);
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  const handleSave = async () => {
    if (!user || !title.trim()) return;
    setSaving(true);

    const { data, error } = await createWishlist({
      user_id: user.id,
      type,
      title: title.trim(),
      description: description.trim() || undefined,
      event_date: eventDate ? eventDate.toISOString() : undefined,
    });

    if (error) {
      toast({ variant: "destructive", title: "Error", description: "Could not create list." });
    } else if (data) {
      toast({ title: "Created!", description: `${title} has been created.` });
      reset();
      onCreated(data);
    }
    setSaving(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl relative"
          >
            {/* Header */}
            <div className="flex items-center py-4 border-b border-border sticky top-0 bg-background rounded-t-2xl z-10 px-4">
              <div className="w-8" />
              <h2 className="text-lg font-bold text-foreground font-serif flex-1 text-center">
                {t("wishlist.listDetails", { defaultValue: "New List" })}
              </h2>
              <button
                onClick={handleClose}
                className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-5">
              {/* Type selector */}
              <div className="flex items-center gap-1 rounded-xl bg-muted p-1 mb-5">
                {TYPES.map((tp) => {
                  const Icon = tp.icon;
                  const selected = type === tp.value;
                  return (
                    <button
                      key={tp.value}
                      onClick={() => onTypeChange(tp.value)}
                      className={cn(
                        "flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-2.5 text-xs font-medium transition-colors",
                        selected
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <Icon size={14} />
                      {tp.label}
                    </button>
                  );
                })}
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">
                    {t("wishlist.titleLabel", { defaultValue: "Title" })}
                  </Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t("wishlist.titlePlaceholder", { defaultValue: "e.g. Spring Faves" })}
                    className="border-border"
                  />
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">
                    {t("wishlist.eventDate", { defaultValue: "Event Date" })}
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal border-border",
                          !eventDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {eventDate ? format(eventDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={eventDate}
                        onSelect={setEventDate}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">
                    {t("wishlist.description", { defaultValue: "Description" })}
                  </Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What's this list for?"
                    rows={3}
                    className="border-border resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Sticky save button */}
            <div className="sticky bottom-0 bg-background border-t border-border px-5 py-4">
              <Button
                onClick={handleSave}
                disabled={saving || !title.trim()}
                className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                size="lg"
              >
                {saving ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    {t("common.saving", { defaultValue: "Saving..." })}
                  </>
                ) : (
                  t("wishlist.saveList", { defaultValue: "Save Wishlist" })
                )}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CreateWishlistModal;
